// ── Hidden-instruction prompt tags: <vi_prefix>…</vi_prefix> / <vi_suffix>…</vi_suffix>
// A prompt may wrap hidden model instructions in these namespaced tags. The
// content is sent to the model (prefix prepended, suffix appended) but is NEVER
// shown to the user — stripped from the capture input, session bubble, widget
// view, data view and titles. Keep identical to the two other copies of this
// contract: managed-agent-runner/src/managed/hidden-tags.ts and, app-side,
// flutter-neweyes-ai/lib/modules/pages/session/model/session_card.dart.
const VI_PREFIX_BLOCK = /<vi_prefix>[\s\S]*?<\/vi_prefix>/gi;
const VI_SUFFIX_BLOCK = /<vi_suffix>[\s\S]*?<\/vi_suffix>/gi;
const VI_PREFIX_ONE = /<vi_prefix>[\s\S]*?<\/vi_prefix>/i;
const VI_SUFFIX_ONE = /<vi_suffix>[\s\S]*?<\/vi_suffix>/i;

/** True when the text carries any hidden prefix/suffix block. */
export function hasHiddenTags(text) {
  if (!text || typeof text !== 'string') return false;
  return VI_PREFIX_ONE.test(text) || VI_SUFFIX_ONE.test(text);
}

/** DISPLAY transform: drop the tags AND their content → visible text only. */
export function stripHiddenTags(text) {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(VI_PREFIX_BLOCK, '')
    .replace(VI_SUFFIX_BLOCK, '')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

/** The raw `<vi_prefix>…</vi_prefix>` substring (first match) or ''. */
export function hiddenPrefixTag(text) {
  if (!text || typeof text !== 'string') return '';
  const m = text.match(VI_PREFIX_ONE);
  return m ? m[0] : '';
}

/** The raw `<vi_suffix>…</vi_suffix>` substring (first match) or ''. */
export function hiddenSuffixTag(text) {
  if (!text || typeof text !== 'string') return '';
  const m = text.match(VI_SUFFIX_ONE);
  return m ? m[0] : '';
}

/** Capture send re-wrap: prefixTag + visible + suffixTag (so storage keeps the
 *  markers; display strips them, the model gets the content). */
export function composeHiddenSendPrompt(prefill, visible) {
  return hiddenPrefixTag(prefill) + (visible || '') + hiddenSuffixTag(prefill);
}

/** Recursively strip hidden tags from tag-bearing string values in a JSON tree
 *  (tag-free strings pass through UNCHANGED — never trimmed/reflowed). Used at
 *  the widget-runtime data-injection boundary. */
export function stripHiddenTagsDeep(value) {
  if (typeof value === 'string') return hasHiddenTags(value) ? stripHiddenTags(value) : value;
  if (Array.isArray(value)) return value.map(stripHiddenTagsDeep);
  if (value && typeof value === 'object') {
    const out = {};
    for (const k of Object.keys(value)) out[k] = stripHiddenTagsDeep(value[k]);
    return out;
  }
  return value;
}

export function getShortTitle(prompt) {
  if (!prompt || typeof prompt !== 'string') return 'New Session';
  // Strip hidden <vi_prefix>/<vi_suffix> instruction tags first — titles derive
  // from the prompt, the hidden content must never surface in a title.
  // Strip [USER_DISPATCH] prefix
  let text = stripHiddenTags(prompt).replace(/^\[USER_DISPATCH\]\s*/i, '');
  // Strip [PHOTO CONTEXT ...] blocks
  text = text.replace(/\[PHOTO CONTEXT[^\]]*\][\s\S]*?\[END PHOTO CONTEXT\]/gi, '');
  // Strip photo URLs
  text = text.replace(/https:\/\/storage\.googleapis\.com\/\S+/g, '');
  // Strip "intention:" prefix
  text = text.replace(/^intention:\s*/i, '');
  // Clean up Photos: sections and dashes
  text = text.replace(/\nPhotos?:?\s*[\s\S]*/i, '');
  text = text.replace(/Photos:\s*(-\s*\n?)*/g, '');
  // Take first line
  text = text.split('\n')[0].trim();
  // Filter greeting phrases — return generic title instead
  if (/^(hello|hi|hey|how can i help|what can i do)/i.test(text)) return 'Photo Analysis';
  if (!text || text.length < 3) return 'New Session';
  // CJK-aware truncation
  const cjkChars = text.match(/[\u4e00-\u9fff\u3040-\u309f\u30a0-\u30ff]/g);
  const isCJK = cjkChars && cjkChars.length > text.length * 0.3;
  if (isCJK) {
    return text.length <= 8 ? text : text.slice(0, 8) + '...';
  }
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length <= 4) return text;
  return words.slice(0, 4).join(' ') + '...';
}
